/**
 * getHTML({ includeStyles }) 결과의 `<style>` 를 저장 본문에 보존하기 위한 재스코프 추출.
 *
 * 문제:
 * - `DOMParser.parseFromString(html, 'text/html')` 는 선두 `<style>` 를 `<head>` 로 올리므로
 *   `doc.body` 만 직렬화하면 `<style>` 가 누락된다.
 * - 최종 저장 HTML 에서는 `.ngw-content` 래퍼가 `wrapEmailBodyHTML` 의 region 재조립 과정에서
 *   제거되므로, 셀렉터의 `.ngw-content` 스코프도 함께 제거해야 저장 본문에서 스타일이 적용된다.
 *
 * 정책:
 * - `.ngw-content` 스코프를 제거하고, **bare element 규칙(예: `p { margin:0 }`, `blockquote {…}`)도
 *   포함**한다. 라이브러리 기본 리셋(특히 `p` 의 margin/padding)이 저장/전송 본문에 보존되도록 하기
 *   위함이다. (과거에는 bare element 를 전부 제외했으나, 그로 인해 `p` 정의가 소실돼 전송 본문의
 *   문단 여백이 편집기와 달라지는 문제가 있었다 — bare element 전체 제외는 해법이 아님.)
 * - 단, `.ngw-content` 제거 후 **선행 combinator(`>`/`+`/`~`)만 남는 규칙**(예:
 *   `.ngw-content > *:not(:first-child)`)은 앵커를 잃어 단독 셀렉터로 유효하지 않으므로 제외한다.
 * - ⚠️ 알려진 트레이드오프: bare `blockquote {…}` 리셋이 외부 인용문(`quoted_body` 내 `<blockquote>`)을
 *   오염시킬 수 있다. 실제 문제가 확인되면 **그 때 blockquote 규칙만 선별 제외**한다(bare element 전체
 *   제외로 되돌리지 않는다).
 * - 동일 셀렉터는 하나로 병합한다.
 */
export function extractVariantStyleCssFromDoc(doc: Document): string {
  const styleEls = Array.from(doc.querySelectorAll('style'))
  if (styleEls.length === 0) return ''
  const rawCss = styleEls.map((s) => s.textContent ?? '').join('\n')
  styleEls.forEach((s) => s.parentElement?.removeChild(s))
  if (rawCss.trim().length === 0 || typeof document === 'undefined') return ''

  // live CSSOM 파싱: 임시 <style> 를 main document 에 부착해 cssRules 를 동기 조회한다.
  // media="not all" 로 실제 적용을 막고, `.ngw-content` 스코프라 라이브 앱에 매칭 요소가 없으며
  // finally 에서 즉시 제거하므로 부작용이 없다.
  const probe = document.createElement('style')
  probe.media = 'not all'
  probe.textContent = rawCss
  document.head.appendChild(probe)
  try {
    const sheet = probe.sheet
    if (sheet == null) return ''
    const merged = new Map<string, string>()
    const order: string[] = []
    for (const rule of Array.from(sheet.cssRules)) {
      if (rule.constructor.name !== 'CSSStyleRule') continue
      let selectorText: string
      let body: string
      try {
        selectorText = (rule as CSSStyleRule).selectorText
        body = (rule as CSSStyleRule).style.cssText
      } catch {
        continue
      }
      if (body.length === 0) continue
      const kept = selectorText
        .split(',')
        .map((s) => s.replace(/\.ngw-content\b\s*/g, '').trim())
        // bare element 포함(정책 참조). 단 `.ngw-content` 제거로 선행 combinator(>/+/~)만 남아
        // 앵커를 잃은 규칙은 단독으로 무효이므로 제외한다.
        .filter((s) => s.length > 0 && !/^[>+~]/.test(s))
      if (kept.length === 0) continue
      const sel = kept.join(', ')
      const prev = merged.get(sel)
      if (prev != null) {
        merged.set(sel, `${prev}${body}`)
      } else {
        merged.set(sel, body)
        order.push(sel)
      }
    }
    return order.map((sel) => `${sel} { ${merged.get(sel)} }`).join('\n')
  } catch {
    return ''
  } finally {
    probe.remove()
  }
}
